import React from 'react'
import { Card, CardContent, Typography } from '@material-ui/core'
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined'
import {
  ListedItemConditionBadge,
  ListedItemPurchaseBadge,
  ListedItemStateBadge,
} from './listed-item-badges'
import { ListedItemProps } from './listed-item.interfaces'
import { useListedItemStyles } from './listed-item.styles'
import { Link } from 'react-router-dom'
import {
  currencyTransform,
  CurrencyList,
  Locale,
} from '../../utils/currency-transform'
import { NoPhotographyOutlinedIcon } from '../../assets/svg/no-photography-outlined-icon'
import { ImageCard } from '../image-card'
import { useTranslation } from 'react-i18next'
import { ItemState } from '../../models'
import { getDateByState } from '../../utils/item-details'
import { OrderStatus } from '../../pages/order-page/order-header/order-header.constants'

export const ListedItem: React.FC<ListedItemProps> = (
  props: ListedItemProps
): React.ReactElement => {
  const {
    defaultImageId,
    name,
    condition,
    id,
    price,
    city,
    region,
    locale = Locale.US,
    state,
    currency = CurrencyList.USD,
    settings,
    showDate,
    soldUtc,
    createdUtc,
    listedUtc,
    itemUrl,
    isPurchase,
    showSoldDate = false,
    orderStatus,
    showOrderStatus = false,
    isModerator = false,
  } = props

  const classes = useListedItemStyles()

  const linkTo = itemUrl
    ? itemUrl
    : isModerator
      ? `/moderator-view-listed-items/${id}`
      : `/listed-items/${id}`

  const { t } = useTranslation()

  const getDateTitle = (): string => {
    const TITLE_LIST = {
      [ItemState.Draft]: t('filter.dateBlock.created'),
      [ItemState.Active]: t('filter.dateBlock.listed'),
      [ItemState.Sold]: t('filter.dateBlock.sold'),
    }

    return (
      TITLE_LIST[state as keyof typeof TITLE_LIST] ??
      t('filter.dateBlock.created')
    )
  }

  return (
    <Card className={classes.card} elevation={0}>
      <CardContent className={classes.cardContent}>
        <div className={classes.cardHeader}>
          <Link to={linkTo} className={classes.link}>
            {defaultImageId ? (
              <ImageCard imageId={defaultImageId} />
            ) : (
              <div className={classes.imgHolder}>
                <div className={classes.iconWrapper}>
                  <NoPhotographyOutlinedIcon
                    style={{ fontSize: 90, color: 'black' }}
                  />
                </div>
              </div>
            )}
          </Link>
          {settings && settings.hideCondition ? null : (
            <ListedItemConditionBadge content={condition} />
          )}
          <ListedItemStateBadge content={state} />
          <ListedItemPurchaseBadge content={Boolean(isPurchase)} />
        </div>
        <Link
          to={linkTo}
          className={`${classes.noneTextDecoration} ${classes.productTitleLink}`}
        >
          <Typography
            variant="inherit"
            component="h5"
            className={classes.productTitle}
            title={name}
          >
            {name}
          </Typography>
        </Link>
        <div>
          {settings && settings.hideCondition ? null : (
            <div
              className={`${classes.productPropertyLine} ${classes.productPropertyLineOffset}`}
            >
              <Typography className={classes.conditionLabel}>
                Condition:{' '}
                <span
                  className={`${classes.conditionValue} ${
                    condition ? classes.conditionValueUsed : classes.conditionValueNew
                  }`}
                >
                  {condition
                    ? t('listedItemCard.condition.used')
                    : t('listedItemCard.condition.new')}
                </span>
              </Typography>
            </div>
          )}
          <div
            className={`${classes.productPropertyLine} ${classes.productPropertyLineOffset}`}
          >
            <Typography className={classes.propertyValue}>
              {currencyTransform(price, locale, {
                style: 'currency',
                currency,
              })}
            </Typography>
          </div>
          {settings && settings.hideCity && settings.hideRegion ? null : (
            <div
              className={`${classes.productPropertyLine} ${classes.productPropertyLineOffset}`}
            >
              <div className={classes.cityRow}>
                <LocationOnOutlinedIcon
                  className={classes.locationIcon}
                  aria-hidden
                />
                <Typography className={classes.cityValue} component="span">
                  {`${city}, ${region}`}
                </Typography>
              </div>
            </div>
          )}
        </div>
        {showDate && (
          <div
            className={classes.dateBlock}
            data-hidden={state === ItemState.Draft ? 'true' : undefined}
          >
            <span className={classes.dateLabel}>{getDateTitle()}</span>
            <span>
              {getDateByState(
                { createdUtc, listedUtc, soldUtc },
                state as ItemState
              )}
            </span>
          </div>
        )}

        {showSoldDate && (
          <div className={`${classes.dateBlock} ${classes.orderInfoRow}`}>
            <span className={classes.dateLabel}>Bought:</span>
            <span className={classes.dateBlockValue}>
              {getDateByState(
                { createdUtc, listedUtc, soldUtc },
                ItemState.Sold as ItemState
              )}
            </span>
          </div>
        )}

        {showOrderStatus && (
          <div className={`${classes.orderStatusBlock} ${classes.orderInfoRow}`}>
            <span className={classes.dateLabel}>Order Status:</span>
            <span className={classes.orderStatusBlockValue}>
              {orderStatus && OrderStatus.get(orderStatus)}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
